import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { SidebarItem } from '@/types/navigation';
import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';
import { SidebarFlyout } from './SidebarFlyout';
import { hasLabeledGroups, menuGroupLabelAt, startsMenuGroup } from './menu-groups';

interface SidebarDropdownProps {
  items: SidebarItem[];
  title: string;
  direction: 'up' | 'down';
  triggerEl: HTMLDivElement | null;
  isOpen: boolean;
  onClose: () => void;
}

/** Casa a rota atual com o `href` de um item — segmento inteiro, nunca prefixo solto. */
const matchesPath = (pathname: string, href?: string): boolean => {
  if (!href) return false;
  if (href === '/') return pathname === '/';
  return pathname === href || pathname.startsWith(`${href}/`);
};

/**
 * `inert` só existe como prop tipada no React 19; aqui (React 18) o atributo
 * precisa ir como string vazia. Tira o painel fora de vista do tab order e da
 * árvore de acessibilidade SEM desmontá-lo (desmontar traria de volta o vão
 * branco durante a transição de "voltar").
 */
const inertWhen = (on: boolean): Record<string, string> => (on ? { inert: '' } : {});

/**
 * Menu de nav da sidebar (desktop). Um item filho que TAMBÉM tem filhos NÃO
 * abre um 3º dropdown aninhado: o painel DESLIZA para o submenu (mesmo efeito
 * da gaveta mobile), com "voltar" no topo. Vale para qualquer filho-com-filhos,
 * mas com **um único nível** de profundidade — o desktop guarda um `subId`, não
 * uma pilha como a gaveta mobile (hoje: Help Desk → Checklists).
 *
 * A transição respeita `prefers-reduced-motion` pela regra global do index.css
 * (`transition-duration: .01ms !important`).
 */
export const SidebarDropdown: React.FC<SidebarDropdownProps> = ({
  items,
  title,
  direction,
  triggerEl,
  isOpen,
  onClose,
}) => {
  const location = useLocation();

  // Submenu aberto dentro do flyout (id do filho-com-filhos). null = raiz.
  const [subId, setSubId] = useState<string | null>(null);
  // Reabrir o menu sempre começa na raiz (ciclo pai→filho do DS §Overlays).
  useEffect(() => {
    if (!isOpen) setSubId(null);
  }, [isOpen]);

  const subItem = subId ? items.find((it) => it.id === subId) ?? null : null;

  /**
   * O flyout normalmente tem largura shrink-to-fit (minWidth 240). Com a esteira
   * de 2 painéis lado a lado isso QUEBRA: a largura preferida vira a soma dos
   * dois painéis (percentuais contam como `auto` no cálculo intrínseco) e o menu
   * abre ~2x mais largo. Menus COM submenu recebem largura definida — assim
   * `width: 100%` da esteira e dos painéis resolve contra um valor concreto.
   * Menus sem submenu (ex.: Configurações) seguem shrink-to-fit, inalterados.
   */
  const hasNestedMenu = items.some((it) => !!it.children && it.children.length > 0);
  const NESTED_FLYOUT_WIDTH = 256;

  /**
   * Realce: vence o href MAIS ESPECÍFICO da árvore do menu. Sem isso "Aplicações"
   * (`/checklists`) destacaria junto com "Modelos" (`/checklists/templates`) —
   * o mesmo empate que já existia entre "Pipelines" (`/crm`) e "Leads"
   * (`/crm/leads`). Grupo destaca por herança do descendente ativo.
   */
  const activeId = useMemo(() => {
    const matches: Array<{ id: string; len: number; depth: number }> = [];
    const visit = (list: SidebarItem[], depth: number): void => {
      for (const it of list) {
        if (it.href && matchesPath(location.pathname, it.href)) matches.push({ id: it.id, len: it.href.length, depth });
        if (it.children) visit(it.children, depth + 1);
      }
    };
    visit(items, 0);
    // href mais longo vence; EMPATE de comprimento vai pro mais PROFUNDO. O
    // empate é real: em `/checklists` o grupo "Checklists" e o filho "Aplicações"
    // têm o MESMO href, e o ancestral (que nem é renderizado no painel 2) roubaria
    // o realce da folha. O grupo continua aceso por herança via `isActiveItem`.
    matches.sort((a, b) => b.len - a.len || b.depth - a.depth);
    return matches[0]?.id ?? null;
  }, [items, location.pathname]);

  const isActiveItem = (item: SidebarItem): boolean =>
    item.id === activeId || !!item.children?.some((child) => isActiveItem(child));

  const renderItem = (child: SidebarItem) => {
    const hasChildren = !!child.children && child.children.length > 0;
    const childIsActive = isActiveItem(child);
    const hasIcon = !!child.icon || !!child.iconName;

    const itemStyle: React.CSSProperties = {
      backgroundColor: childIsActive ? '#f1f5f9' : 'transparent',
      color: childIsActive ? '#1e293b' : '#374151',
      textDecoration: 'none',
      paddingLeft: hasIcon ? undefined : '28px',
    };

    const handleMouseEnter = (e: React.MouseEvent<HTMLElement>) => {
      if (!childIsActive) {
        e.currentTarget.style.color = '#1e293b';
        e.currentTarget.style.backgroundColor = '#f1f5f9';
      }
    };

    const handleMouseLeave = (e: React.MouseEvent<HTMLElement>) => {
      if (!childIsActive) {
        e.currentTarget.style.color = '#374151';
        e.currentTarget.style.backgroundColor = 'transparent';
      }
    };

    const iconEl = child.iconName ? (
      <span className="flex-shrink-0 mr-3" style={{ color: '#64748b' }}>
        <Icon name={child.iconName as IconName} className="h-[16px] w-[16px]" />
      </span>
    ) : null;

    // Filho COM filhos: desliza para o submenu (nunca navega, nunca abre 3º dropdown).
    // `role`/`tabIndex`/`onKeyDown`: é o ÚNICO caminho até os netos no desktop —
    // sem eles o submenu ficava inalcançável por teclado.
    if (hasChildren) {
      return (
        <div
          key={child.id}
          role="button"
          tabIndex={0}
          aria-expanded={subId === child.id}
          className="flex items-center text-sm font-medium rounded-md cursor-pointer transition-all duration-150 p-2"
          style={itemStyle}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={() => setSubId(child.id)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setSubId(child.id);
            }
          }}
          data-testid={`sidebar-flyout-group-${child.id}`}
        >
          {iconEl}
          <span className="flex-1 min-w-0 truncate">{child.label}</span>
          <Icon name="chevron-right" className="h-[14px] w-[14px] flex-shrink-0 ml-2" style={{ color: '#94a3b8' }} />
        </div>
      );
    }

    // Items with onClick (no navigation) render as div
    if (child.onClick && !child.href) {
      return (
        <div
          key={child.id}
          className="flex items-center text-sm font-medium rounded-md cursor-pointer transition-all duration-150 p-2"
          style={itemStyle}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={() => {
            child.onClick?.();
            onClose();
          }}
          data-testid={`sidebar-flyout-item-${child.id}`}
        >
          {iconEl}
          {child.label}
        </div>
      );
    }

    return (
      <Link
        key={child.id}
        to={child.href || '#'}
        className="flex items-center text-sm font-medium rounded-md cursor-pointer transition-all duration-150 p-2"
        style={itemStyle}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={() => onClose()}
        data-testid={`sidebar-flyout-item-${child.id}`}
      >
        {iconEl}
        {child.label}
      </Link>
    );
  };

  const captionStyle: React.CSSProperties = {
    color: '#64748b',
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
  };

  /**
   * Lista de itens com a linha (e, quando o bloco tem nome, o rótulo) entre
   * blocos. Recebe a lista JÁ filtrada por permissão, então `startsMenuGroup`
   * compara sempre com o anterior VISÍVEL — bloco que perdeu o primeiro item
   * ganha a linha no próximo que sobrou, e bloco que sumiu inteiro não deixa
   * linha órfã.
   *
   * Bloco COM rótulo dispensa a linha: o próprio título já separa, e desenhar
   * os dois deixa o menu listrado.
   */
  const renderList = (list: SidebarItem[]): React.ReactNode =>
    list.map((child, index) => {
      const groupLabel = menuGroupLabelAt(list, index);
      return (
        <React.Fragment key={child.id}>
          {groupLabel !== null ? (
            <div
              // MESMAS classes do título de menu (linha do `{title}` abaixo):
              // o rótulo de bloco ocupa o lugar dele, então precisa da mesma
              // altura — senão os itens do submenu nascem alguns pixels acima
              // dos de CRM, Help Desk e Prospecção, e o menu fica desalinhado
              // com o resto do sistema.
              className="px-3 py-2 mb-1"
              style={captionStyle}
              data-testid={`sidebar-flyout-group-label-${child.group ?? ''}`}
            >
              {groupLabel}
            </div>
          ) : (
            startsMenuGroup(list, index) && (
              <div
                role="separator"
                data-testid={`sidebar-flyout-separator-${child.id}`}
                style={{ height: 1, margin: '6px 8px', backgroundColor: '#e5e7eb' }}
              />
            )
          )}
          {renderItem(child)}
        </React.Fragment>
      );
    });

  return (
    <SidebarFlyout
      triggerEl={triggerEl}
      direction={direction}
      isOpen={isOpen}
      onClose={onClose}
      width={hasNestedMenu ? NESTED_FLYOUT_WIDTH : undefined}
    >
      {/* Esteira deslizante: 2 painéis de 100% da largura do flyout; -100%
          quando um submenu está aberto (mesma anatomia da gaveta mobile). */}
      <div style={{ overflow: 'hidden' }}>
        <div
          className="flex items-start"
          style={{
            width: '100%',
            transform: subItem ? 'translateX(-100%)' : 'none',
            transition: 'transform .22s ease',
          }}
        >
          {/* PAINEL 1 — raiz do menu. Fora de vista = fora do tab order e da
              árvore de acessibilidade (aria-hidden + inert), sem desmontar. */}
          <div
            className="flex-none w-full"
            aria-hidden={!!subItem}
            data-testid="sidebar-flyout-panel-root"
            {...inertWhen(!!subItem)}
          >
            {/* O título do menu só aparece quando os blocos NÃO têm nome. Com
                blocos nomeados ("Gestão", "Usuário"), repetir "Dashboards" em
                cima somaria um terceiro cabeçalho que não diz nada de novo —
                o flyout já está ancorado no item que o abriu. */}
            {!hasLabeledGroups(items) && (
              <div className="px-3 py-2 mb-1" style={captionStyle}>
                {title}
              </div>
            )}
            {renderList(items)}
          </div>

          {/* PAINEL 2 — submenu. Só existe em menus COM submenu: renderizá-lo em
              menu plano (Configurações, CRM…) somaria a largura dele no cálculo
              intrínseco do flyout e alargaria menus que não mudaram. */}
          {hasNestedMenu && (
            <div
              className="flex-none w-full"
              aria-hidden={!subItem}
              data-testid="sidebar-flyout-panel-sub"
              {...inertWhen(!subItem)}
            >
              <button
                type="button"
                onClick={() => setSubId(null)}
                className="flex items-center gap-1.5 w-full text-left px-3 py-2 mb-1 rounded-md"
                style={captionStyle}
                aria-label="Voltar"
                data-testid="sidebar-flyout-back"
              >
                <Icon name="chevron-left" className="h-[14px] w-[14px] flex-shrink-0" />
                <span className="min-w-0 truncate">{subItem?.label}</span>
              </button>
              {renderList(subItem?.children ?? [])}
            </div>
          )}
        </div>
      </div>
    </SidebarFlyout>
  );
};
